import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    ChartLine,
    Trophy,
    Fire,
    CheckCircle,
    Funnel,
    Users,
    Clock,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Performance() {
    const [digest, setDigest] = useState(null);
    const [ops, setOps] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, o] = await Promise.all([
                axios.get(`${API}/stats/digest`),
                axios.get(`${API}/stats/weekly/by-operator`),
            ]);
            setDigest(d.data);
            setOps(o.data);
        } catch (e) {
            console.error("performance load fail", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="min-h-screen bg-white bg-grid">
            <main className="p-6 lg:p-8 max-w-[1400px] mx-auto w-full">
                <div className="mb-6">
                    <div className="label-kicker mb-2 flex items-center gap-2">
                        <ChartLine size={12} weight="bold" />
                        Weekly Performance
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        This week's
                        <br />
                        <span className="underline decoration-[3px]">team output</span>.
                    </h1>
                    <p className="mt-3 text-[#52525b] text-sm max-w-xl">
                        Funnel, top categories, and per-operator activity across the last
                        seven days — computed live from status-history events.
                    </p>
                </div>

                {loading && <div className="panel p-10 text-center text-sm text-[#52525b]">Loading…</div>}

                {!loading && digest && (
                    <>
                        {/* Funnel */}
                        <section
                            className="panel mb-6"
                            data-testid="digest-funnel"
                        >
                            <div className="panel-header">
                                <div className="flex items-center gap-2">
                                    <Funnel size={14} weight="bold" />
                                    <span className="label-kicker">Weekly Funnel</span>
                                </div>
                                {digest.best_operator && (
                                    <span
                                        className="mono text-[11px] text-[#002FA7]"
                                        data-testid="best-operator"
                                    >
                                        <Trophy size={12} weight="bold" className="inline mr-1" />
                                        Top: {digest.best_operator}
                                    </span>
                                )}
                            </div>
                            <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-3">
                                <FunnelTile
                                    Icon={Fire}
                                    label="New providers"
                                    value={digest.funnel.new_providers}
                                    color="#000"
                                    testId="funnel-new"
                                />
                                <FunnelTile
                                    Icon={Users}
                                    label="Contacted"
                                    value={digest.funnel.contacted}
                                    color="#52525b"
                                    testId="funnel-contacted"
                                />
                                <FunnelTile
                                    Icon={CheckCircle}
                                    label="Interested"
                                    value={digest.funnel.interested}
                                    color="#002FA7"
                                    testId="funnel-interested"
                                />
                                <FunnelTile
                                    Icon={Trophy}
                                    label="Claimed"
                                    value={digest.funnel.claimed}
                                    color="#000"
                                    testId="funnel-claimed"
                                />
                                <FunnelTile
                                    Icon={Clock}
                                    label="Invalid"
                                    value={digest.funnel.invalid}
                                    color="#ff3333"
                                    testId="funnel-invalid"
                                />
                            </div>
                        </section>

                        {/* Top categories */}
                        {digest.top_categories?.length > 0 && (
                            <section
                                className="panel mb-6"
                                data-testid="top-categories"
                            >
                                <div className="panel-header">
                                    <span className="label-kicker">Top Categories This Week</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="brutal-table">
                                        <thead>
                                            <tr>
                                                <th>Category · Location</th>
                                                <th>New</th>
                                                <th>Contacted</th>
                                                <th>Interested</th>
                                                <th>Claimed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {digest.top_categories.map((c, i) => (
                                                <tr key={c.key || i} data-testid={`top-cat-${i}`}>
                                                    <td>
                                                        <div className="font-medium">
                                                            {c.category} · {c.location}
                                                        </div>
                                                    </td>
                                                    <td className="mono">{c.new}</td>
                                                    <td className="mono">{c.contacted}</td>
                                                    <td className="mono" style={{ color: "#002FA7" }}>
                                                        {c.interested}
                                                    </td>
                                                    <td className="mono" style={{ color: "#000", fontWeight: 700 }}>
                                                        {c.claimed}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}
                    </>
                )}

                {!loading && ops && (
                    <section
                        className="panel"
                        data-testid="by-operator"
                    >
                        <div className="panel-header">
                            <div className="flex items-center gap-2">
                                <Users size={14} weight="bold" />
                                <span className="label-kicker">By Operator (last 7 days)</span>
                                <span className="mono text-[11px] text-[#52525b]">
                                    {ops.total_operators || 0} operators
                                </span>
                            </div>
                        </div>
                        {ops.operators.length === 0 ? (
                            <div className="p-10 text-center text-sm text-[#52525b]">
                                No operator activity in the last 7 days.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="brutal-table">
                                    <thead>
                                        <tr>
                                            <th>Operator</th>
                                            <th>Total actions</th>
                                            <th>Contacted</th>
                                            <th>Interested</th>
                                            <th>Claimed</th>
                                            <th>Invalid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ops.operators.map((o, i) => (
                                            <tr
                                                key={o.operator}
                                                data-testid={`operator-row-${o.operator}`}
                                            >
                                                <td>
                                                    <div className="font-medium flex items-center gap-2">
                                                        {o.operator}
                                                        {i === 0 && ops.operators.length > 1 && (
                                                            <span
                                                                className="mono text-[9px] px-1.5 py-0.5 bg-black text-white"
                                                                data-testid={`top-operator-badge`}
                                                            >
                                                                TOP
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="mono">{o.total}</td>
                                                <td className="mono">{o.contacted}</td>
                                                <td className="mono" style={{ color: "#002FA7" }}>
                                                    {o.interested}
                                                </td>
                                                <td className="mono font-bold">{o.claimed}</td>
                                                <td className="mono" style={{ color: "#ff3333" }}>
                                                    {o.invalid}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
}

function FunnelTile({ Icon, label, value, color, testId }) {
    return (
        <div
            className="border border-[#e4e4e7] p-3 flex flex-col gap-1"
            data-testid={testId}
        >
            <span
                className="label-kicker flex items-center gap-1"
                style={{ fontSize: 9 }}
            >
                <Icon size={11} weight="bold" />
                {label}
            </span>
            <span className="font-display text-3xl font-bold" style={{ color }}>
                {value ?? 0}
            </span>
        </div>
    );
}
